import { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector } from "react-redux";
import JoditEditor from 'jodit-react';
import { Checkbox } from "@mui/material";
import axios from 'axios';
import { selectCurrentUserApiToken } from "../slices/currentUser/currentUserSlice";
import server_config from '../ServerConfiguration';

const fevir_node_server_address = server_config["fevir_node_server_address"];

const editorConfig = {
  placeholder: 'Scratchpad to put any temporary notes here.',
  allowResizeX: false,
  allowResizeY: false,
  readonly: false,
  height: '100% !important',
  showCharsCounter: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  addNewLine: false,
  style: {
    textAlign: 'left'
  }
};

const SidePanel = ({ isOpen }) => {
  const apiToken = useSelector(selectCurrentUserApiToken);
  const editorRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [autoSaveChecked, setAutoSaveChecked] = useState(true);

  // Use a state variable for the editor's content
  const [editorContent, setEditorContent] = useState('');

  // Use a ref to store the last saved content for comparison
  const lastSavedContentRef = useRef('');

  const saveScratchPad = useCallback(async (contentToSave = editorContent) => {
    if (!apiToken) return;
    try {
      const body = {
        'functionid': "savescratchpad",
        'settings': { 'autosavechecked': autoSaveChecked },
        'apiToken': apiToken
      };
      if (autoSaveChecked) {
        body['note'] = contentToSave;
      }
      await axios.post(fevir_node_server_address, body, {
        timeout: 10000
      });
      lastSavedContentRef.current = contentToSave;
    } catch (error) { console.error("Failed to save scratchpad:", error); }
  }, [apiToken, autoSaveChecked, editorContent]);

  const saveSettings = useCallback(async () => {
    if (!apiToken) return;
    try {
      const body = {
        'functionid': "savescratchpad",
        'settings': { 'autosavechecked': autoSaveChecked },
        'apiToken': apiToken
      };
      await axios.post(fevir_node_server_address, body, {
        timeout: 10000
      });
    } catch (error) { console.error("Failed to save settings:", error); }
  }, [apiToken, autoSaveChecked]);

  useEffect(() => {
    if (!apiToken) {
      setIsLoaded(true);
    }
    if (!isOpen || !apiToken) return;

    const loadData = async () => {
      try {
        const resp = await axios.post(fevir_node_server_address, {
          'functionid': "retrievescratchpad",
          'apiToken': apiToken
        }, { timeout: 10000 });

        if (resp?.data?.success) {
          const loadedContent = resp.data.previousNote || "";
          const loadedAutoSave = resp.data.autosavechecked ?? true;

          setEditorContent(loadedContent);
          lastSavedContentRef.current = loadedContent;
          setAutoSaveChecked(loadedAutoSave);
          setIsLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load scratchpad:", error);
        setIsLoaded(true);
      }
    };

    loadData();
  }, [isOpen, apiToken]);

  useEffect(() => {
    let intervalId;
    if (autoSaveChecked && isLoaded) {
      intervalId = setInterval(() => {
        if (editorContent !== lastSavedContentRef.current) {
          saveScratchPad();
        }
      }, 500); //Every half-second
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [autoSaveChecked, isLoaded, saveScratchPad, editorContent]);

  useEffect(() => {
    if (isLoaded && !isOpen && editorContent !== lastSavedContentRef.current) {
      saveScratchPad();
    }
  }, [isOpen, isLoaded, saveScratchPad, editorContent]);

  useEffect(() => {
    if (isLoaded) {
      saveSettings();
    }
  }, [autoSaveChecked, isLoaded, saveSettings]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (autoSaveChecked && editorContent !== lastSavedContentRef.current) {
        axios.post(fevir_node_server_address, {
          'functionid': "savescratchpad",
          'settings': { 'autosavechecked': autoSaveChecked },
          'apiToken': apiToken,
          'note': editorContent
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [apiToken, autoSaveChecked, editorContent]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="side-panel pagewrapper">
      {isLoaded ? (
        <JoditEditor
          ref={editorRef}
          value={editorContent}
          config={editorConfig}
          tabIndex={1}
          onChange={newContent => setEditorContent(newContent)}
        />
      ) : (
        <div>Loading scratchpad...</div>
      )}
      <div style={{ marginTop: "auto", textAlign: "left" }}>
        {(apiToken && isLoaded) && (
          <>
            <Checkbox
              style={{ pointerEvents: "auto" }}
              checked={autoSaveChecked}
              onChange={(e) => setAutoSaveChecked(e.target.checked)}
              color="primary"
              label="Auto-save"
              name="Auto-save"
              inputProps={{ 'aria-label': 'Auto-save' }}
            />
            Auto-save
          </>
        )}
      </div>
    </div>
  );
};

export default SidePanel;